import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import NayaxRecapImportDialog from "./NayaxRecapImportDialog";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const monthLabel = (ym) => { const [y, m] = ym.split("-"); return `${MONTHS[Number(m) - 1]} ${y.slice(2)}`; };
const chf = (n) => Number(n || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qty = (n) => Number(n || 0).toLocaleString("fr-CH", { maximumFractionDigits: 0 });
const currentYear = new Date().getFullYear();
const YEARS = [currentYear, currentYear - 1, currentYear - 2];

const signed = (n) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${chf(Math.abs(n))}`;

function Gap({ c }) {
  const diff = c.ca - (c.am_ca || 0);
  const big = Math.abs(diff) > 20 && Math.abs(diff) > Math.abs(c.ca) * 0.02;
  return (
    <div className={`text-[10px] ${big ? "text-[#C05C47] font-semibold" : "text-[#8A8C88]"}`} data-testid="nayax-cell-gap">
      AM {chf(c.am_ca)} · écart {signed(diff)}
    </div>
  );
}

function Split({ c }) {
  if (c.ca_twint == null) return <div className="text-[10px] text-[#8A8C88]">répartition inconnue</div>;
  const pct = c.ca ? Math.round((c.ca_twint / c.ca) * 100) : 0;
  return (
    <div className="text-[10px] text-[#575A57] leading-tight" data-testid="sales-split">
      <div>Cartes {chf(c.ca_cards)}</div>
      <div className="text-[#0B6E99]">TWINT {chf(c.ca_twint)} ({pct} %)</div>
      {c.tx_twint != null && <div className="text-[#0B6E99]">{qty(c.tx_twint)} tx TWINT</div>}
    </div>
  );
}

function Cell({ c, bold, unit = "barq.", showGap = false, showSplit = false }) {
  if (!c || (!c.qty && !c.ca)) return <td className="px-2 py-2 text-center text-[#C8C8C0]">—</td>;
  const nayax = c.source === "NAYAX";
  return (
    <td className={`px-2 py-2 text-right tabular-nums whitespace-nowrap ${bold ? "font-semibold" : ""}`}
      title={nayax ? "Chiffre du récap Nayax importé" : undefined}>
      <div>
        {nayax && <span className="mr-1 px-1 rounded bg-[#EEE9FA] text-[#5847A6] text-[9px] font-semibold align-middle" data-testid="nayax-cell-badge">Nayax</span>}
        {chf(c.ca)}
      </div>
      <div className="text-[11px] text-[#575A57]">{qty(c.qty)} {nayax ? "tx" : unit}</div>
      {nayax && showGap && <Gap c={c} />}
      {showSplit && <Split c={c} />}
    </td>
  );
}

export default function SalesMonthlyTab() {
  const [period, setPeriod] = useState("last12");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const [showGap, setShowGap] = useState(false);
  const [showSplit, setShowSplit] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/statistics/sales-monthly", { params: period === "last12" ? {} : { year: period } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period, reload]);

  return (
    <div className="space-y-4" data-testid="sales-monthly-tab">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-56" data-testid="sales-monthly-period"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="last12">12 derniers mois</SelectItem>
            {YEARS.map((y) => <SelectItem key={y} value={String(y)}>Année {y}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-[#575A57]">
          CA TTC (CHF) et barquettes vendues, par mois en heure suisse · sans Stock Conthey · « Nayax » = récap importé (tx = transactions)
        </span>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-[#1F4E3D]" />}
        <label className="ml-auto flex items-center gap-2 text-sm text-[#1A1C1A]">
          <Switch checked={showSplit} onCheckedChange={setShowSplit} data-testid="sales-monthly-split-toggle" />
          Cartes / TWINT
        </label>
        <label className="flex items-center gap-2 text-sm text-[#1A1C1A]">
          <Switch checked={showGap} onCheckedChange={setShowGap} data-testid="sales-monthly-gap-toggle" />
          Afficher les écarts
        </label>
        <div><NayaxRecapImportDialog onDone={() => setReload((n) => n + 1)} /></div>
      </div>
      {data && (
        <div className="bg-white border border-[#E5E5E0] rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="sales-monthly-table">
            <thead className="bg-[#F5F5F0] text-xs text-[#575A57]">
              <tr>
                <th className="text-left px-3 py-2 sticky left-0 bg-[#F5F5F0]">Distributeur</th>
                {data.months.map((m) => <th key={m} className="px-2 py-2 text-right whitespace-nowrap">{monthLabel(m)}</th>)}
                <th className="px-3 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && (
                <tr><td colSpan={data.months.length + 2} className="p-6 text-center text-[#575A57]">Aucune vente sur la période.</td></tr>
              )}
              {data.items.map((it) => (
                <tr key={it.distributor_id} className="border-t border-[#EEEEE8]" data-testid={`sales-monthly-row-${it.distributor_id}`}>
                  <td className="px-3 py-2 sticky left-0 bg-white font-medium whitespace-nowrap">
                    {it.distributor_name}{it.archived && <span className="text-[10px] text-[#575A57]"> (archivé)</span>}
                  </td>
                  {data.months.map((m) => <Cell key={m} c={it.months[m]} showGap={showGap} showSplit={showSplit} />)}
                  <Cell c={it.total} bold unit="ventes" showSplit={showSplit} />
                </tr>
              ))}
            </tbody>
            {data.items.length > 0 && (
              <tfoot className="bg-[#F5F5F0] border-t-2 border-[#E5E5E0]" data-testid="sales-monthly-totals">
                <tr>
                  <td className="px-3 py-2 sticky left-0 bg-[#F5F5F0] font-semibold">Total</td>
                  {data.months.map((m) => <Cell key={m} c={data.month_totals[m]} bold unit="ventes" showSplit={showSplit} />)}
                  <Cell c={data.grand_total} bold unit="ventes" showSplit={showSplit} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}
